<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import { SearchBox, BasicButton } from 'wepin-vue-component'
import { WrappedAccountListItem } from '@/components'
import InfoIcon from '@/components/icon/InfoIcon.vue'
import UnsupportNFTIcon from '@/components/icon/UnsupportNFTIcon.vue'
import { useBreakPointsClass } from '@/composables'
import { defaultBreakPoints, defaultCoinIcon } from '@/const'
import router from '@/router'
import { useLocaleStore } from '@/stores'
import type { Account, BaseAccount, BaseCoin, Coin } from '@/types'
import { getParentPath } from '@/utils'

const { t } = useI18n()
const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const localeStore = useLocaleStore()

const props = defineProps<{
  accountList: Account[]
  headerText: string
  excludeTokens?: boolean
}>()
const emit = defineEmits<{
  (event: 'select-account', token: BaseAccount): void
}>()

// props 로 받은 raw account list 를 flat 한다.
const flattenArray = computed(() => {
  return props.accountList.reduce((acc, cur) => {
    acc.push(cur)
    if (!props.excludeTokens && cur.tokens) {
      cur.tokens.forEach((token) => acc.push(token))
    }
    return acc
  }, [] as Array<BaseAccount>)
})

// filter 로직
const searchMethod = ref('')

const filteredList = computed(() => {
  return flattenArray.value.filter((account: BaseAccount) => {
    return (
      account.label.toLowerCase().includes(searchMethod.value.toLowerCase()) ||
      account.coin?.name
        ?.toLowerCase()
        .includes(searchMethod.value.toLowerCase()) ||
      account.coin?.symbol
        .toLowerCase()
        .includes(searchMethod.value.toLowerCase())
    )
  })
})

const selectToken = (token: BaseAccount) => {
  emit('select-account', token)
}

const onClickCreateAccount = (coin: BaseCoin | undefined) => {
  const regex = /^\/dashboard\/([^/]+)/
  const parentRouter = getParentPath(router.currentRoute.value.path)
  const result = parentRouter.match(regex)
  if (!result) {
    return new Error('[onClickCreateAccount] ParentPath None Error')
  }
  router.replace({
    name: result[1] + '_add_account_coinlist',
    query: {
      preSelectedCoin: (coin as Coin)?.id,
    },
  })
}
const evalBalanceRef = ref()
const evalBalanceWidth = ref<number | '52%'>('52%')

useResizeObserver(evalBalanceRef, (entries) => {
  evalBalanceWidth.value = entries[0].contentRect.width
})
</script>
<template>
  <div class="accountSelectWrapper" :class="breakPointsClass">
    <div class="search-bar">
      <div class="search-bar-header">
        <p>{{ headerText }}</p>
        <Popper class="popper-parant" arrow>
          <InfoIcon />
          <template #content>
            <div class="popper-content">
              <UnsupportNFTIcon />{{ t('receive.info_support_nft') }}
            </div>
          </template>
        </Popper>
      </div>
      <SearchBox
        v-model="searchMethod"
        width="400"
        :placeholder="t('send.search')"
      />
    </div>
    <div class="accountList">
      <WrappedAccountListItem
        v-for="account of filteredList"
        :key="account.getKey()"
        :ticker="account.coin?.name ?? ''"
        :symbol="account.coin?.symbol ?? ''"
        :name="account.label"
        :amount="localeStore.applyDigitRule(account.amount.ethValue.toFixed())"
        :value="
          account.amount.calculatePrice(account.coin?.priceInfo).toFixed()
        "
        :support-nft="account.coin?.supportNft"
        :unit="localeStore.localeInfo?.unit!"
        type="list-shortform"
        :ticker-src="account.coin?.iconUrl ?? defaultCoinIcon"
        :network-src="account.coin?.coin?.iconUrl"
        :eval-balance-width="evalBalanceWidth"
        @click="selectToken(account)"
      >
        {{ account.coin?.supportNft }}
      </WrappedAccountListItem>
    </div>
    <div class="bottom-wrapper">
      <BasicButton type="outlined" width="100%" @click="onClickCreateAccount">
        {{ t('my_wallet.add_account') }}
      </BasicButton>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.accountSelectWrapper {
  display: flex;
  flex-flow: column;
  gap: 10px;
  max-width: 460px;
  padding-bottom: 30px;
  & > .search-bar {
    display: grid;
    gap: 20px;
    padding: 0 30px;
    & > .search-bar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      @include typeface('Paragraph_16_r');
      font-weight: 500;
    }
  }
  & > .accountList {
    max-height: 475px;
    overflow-y: auto;
    margin-right: 5px;
    overflow-x: hidden;
    display: inline-block;

    &::-webkit-scrollbar {
      width: 5px;
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--grey_999);
      border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
      background-color: var(--line_01);
      border-radius: 3px;
    }
    &:deep(.listShort) {
      width: 100%;
      cursor: pointer;
      &:hover {
        background-color: var(--bg_main);
      }
    }
  }
}

.bottom-wrapper {
  padding: 0 30px;
}

.popper-content {
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    flex: 0 0 auto;
    margin-right: 8px;
  }
}

.accountSelectWrapper.mobile {
  max-width: none !important;
  width: 100%;
  :deep(.popper) {
    width: 230px;
  }
  & > .search-bar {
    padding: {
      left: 20px;
      right: 20px;
    }
  }
  & > .accountList {
    max-height: fit-content;
    overflow-x: auto;

    &:deep(.listShort) {
      grid-template-columns: 36px 1fr 1fr;
      padding: {
        left: 20px;
        right: 20px;
      }
    }
  }
  .bottom-wrapper {
    padding: 0 20px;
  }
}
.popper-parant {
  & img {
    cursor: pointer;
  }
  // popper content type
  &:deep(.popper) {
    width: max-content;
    background-color: var(--grey_333);
    --popper-theme-background-color-hover: var(--grey_333);
    color: var(--grey_fff);
    border-width: 0px;
    border-style: solid;
    border-radius: 6px;
    padding: 10px;
    box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
    @include typeface('Paragraph_14_r');
    animation: fadein 1s;
  }
  &:deep(.popper #arrow::before),
  &:deep(.popper:hover),
  &:deep(.popper:hover > #arrow::before) {
    background: var(--grey_333);
  }
}

@keyframes fadein {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
